import { captureException, withScope } from "@sentry/vue";
import type { CombinedError, Operation } from "@urql/vue";

type GraphqlErrorKind = "graphql" | "network" | "mixed";
type GraphqlContext = {
  operationName: string;
  operationType: Operation["kind"];
  graphqlErrorCount: number;
  errorPaths?: ReadonlyArray<ReadonlyArray<string | number>>;
  errorCodes?: Array<string | number | boolean>;
  httpStatus?: number;
};

const getOperationName = (operation: Operation) => {
  const operationDefinition = operation.query.definitions.find(
    (definition) => definition.kind === "OperationDefinition",
  );

  return operationDefinition?.name?.value ?? "anonymous";
};

const getErrorKind = (error: CombinedError): GraphqlErrorKind => {
  const hasGraphqlErrors = error.graphQLErrors.length > 0;
  const hasNetworkError = Boolean(error.networkError);

  if (hasGraphqlErrors && hasNetworkError) {
    return "mixed";
  }

  return hasGraphqlErrors ? "graphql" : "network";
};

const isScalarCode = (value: unknown): value is string | number | boolean =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const getHttpStatus = (error: CombinedError) => {
  const status = error.response?.status;
  return typeof status === "number" && Number.isFinite(status)
    ? status
    : undefined;
};

export const reportGraphqlOperationError = (
  error: CombinedError,
  operation: Operation,
) => {
  const operationName = getOperationName(operation);
  const operationType = operation.kind;
  const errorKind = getErrorKind(error);
  const errorPaths = error.graphQLErrors.flatMap((graphqlError) =>
    graphqlError.path ? [graphqlError.path] : [],
  );
  const errorCodes = error.graphQLErrors.flatMap((graphqlError) => {
    const code = graphqlError.extensions.code;
    return isScalarCode(code) ? [code] : [];
  });
  const httpStatus = getHttpStatus(error);
  const context: GraphqlContext = {
    operationName,
    operationType,
    graphqlErrorCount: error.graphQLErrors.length,
  };

  if (errorPaths.length > 0) {
    context.errorPaths = errorPaths;
  }

  if (errorCodes.length > 0) {
    context.errorCodes = errorCodes;
  }

  if (httpStatus !== undefined) {
    context.httpStatus = httpStatus;
  }

  withScope((scope) => {
    scope.setTag("area", "graphql");
    scope.setTag("graphql.operation.name", operationName);
    scope.setTag("graphql.operation.type", operationType);
    scope.setTag("graphql.error.kind", errorKind);
    scope.setContext("graphql", context);
    captureException(error);
  });
};
